import { useTranslation } from 'react-i18next';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormGetValues,
  UseFieldArrayReturn,
  FieldErrors,
} from 'react-hook-form';
import type {
  UpdateCompanyDto,
  CompanyDto,
  CompanyLocationDto,
  IndustrySector,
} from '@monorepo/shared';
import { PrimaryButton } from '../ui/PrimaryButton';
import { AddressForm } from '../form/AddressForm';
import { CompanyInfoForm } from '../form/CompanyInfoForm';
import { DpoForm } from '../form/DpoForm';
import { FacilitiesForm } from '../form/FacilitiesForm';
import { MainCompanyLocationForm } from '../form/MainCompanyLocationForm';
import { LocationsManagement } from '../form/LocationsManagement';
import { ChangeMainContactForm } from '../form/ChangeMainContactForm';
import { CompanySectionLayout } from './CompanySectionLayout';
import { CompanyLogoSection } from './CompanyLogoSection';

export type EditCompanyFormData = UpdateCompanyDto & {
  mainLocation: CompanyLocationDto;
};

interface EditCompanyFormViewProps {
  register: UseFormRegister<EditCompanyFormData>;
  errors: FieldErrors<EditCompanyFormData>;
  setValue: UseFormSetValue<EditCompanyFormData>;
  getValues: UseFormGetValues<EditCompanyFormData>;
  locationsFieldArray: UseFieldArrayReturn<EditCompanyFormData, 'locations'>;
  locationsData: CompanyLocationDto[];
  company: CompanyDto;
  industrySector?: IndustrySector;
  hasDpoDeclaration: boolean;
  hasDataProtectionRepresentative: boolean;
  isExternalConsultant: boolean;
  changeMainContact: boolean;
  successMessage: string;
  isSubmitError: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onRequestSirenUpdate: () => void;
}

export const EditCompanyFormView = ({
  register,
  errors,
  setValue,
  getValues,
  locationsFieldArray,
  locationsData,
  company,
  industrySector,
  hasDpoDeclaration,
  hasDataProtectionRepresentative,
  isExternalConsultant,
  changeMainContact,
  successMessage,
  isSubmitError,
  isSubmitting,
  onSubmit,
  onRequestSirenUpdate,
}: EditCompanyFormViewProps) => {
  const { t } = useTranslation();

  return (
    <>
      <form onSubmit={onSubmit} className="flex flex-col gap-8">
        <CompanySectionLayout title={t('company.section.company_info')}>
          <CompanyInfoForm<EditCompanyFormData>
            register={register}
            errors={errors}
            currentSiren={company.siren}
            onRequestSirenUpdate={onRequestSirenUpdate}
            industrySector={industrySector}
          />
        </CompanySectionLayout>

        <div className="border-t border-gray-100" />

        <CompanySectionLayout title={t('company.section.head_office_address')}>
          <AddressForm
            prefix="headOfficeAddress"
            register={register}
            errors={errors.headOfficeAddress || {}}
            showOptionalInfo={true}
          />
        </CompanySectionLayout>

        <div className="border-t border-gray-100" />

        <CompanySectionLayout title={t('company.section.dpo')}>
          <DpoForm<EditCompanyFormData>
            register={register}
            errors={errors.dataProtectionSettings || {}}
            hasDpoDeclaration={hasDpoDeclaration}
            hasDataProtectionRepresentative={hasDataProtectionRepresentative}
            isExternalConsultant={isExternalConsultant}
          />
        </CompanySectionLayout>

        <div className="border-t border-gray-100" />

        <CompanySectionLayout title={t('company.section.facilities')}>
          <FacilitiesForm<EditCompanyFormData> register={register} />
        </CompanySectionLayout>

        <div className="border-t border-gray-100" />

        <CompanySectionLayout title={t('company.section.main_location')}>
          <MainCompanyLocationForm
            register={register}
            errors={errors.mainLocation || {}}
            siren={company.siren}
          />
        </CompanySectionLayout>

        <div className="border-t border-gray-100" />

        <CompanySectionLayout title={t('company.section.locations')}>
          <LocationsManagement<EditCompanyFormData>
            register={register}
            setValue={setValue}
            getValues={getValues}
            errors={{ locations: errors.locations }}
            fieldArray={locationsFieldArray}
            siren={company.siren}
            locationsData={locationsData}
          />
        </CompanySectionLayout>

        <div className="border-t border-gray-100" />

        <CompanySectionLayout title={t('company.section.change_main_contact')}>
          <ChangeMainContactForm<EditCompanyFormData>
            register={register}
            errors={errors}
            changeMainContact={changeMainContact}
          />
        </CompanySectionLayout>

        {successMessage && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">{successMessage}</p>
          </div>
        )}

        {isSubmitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{t('company.error.submit_failed')}</p>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <PrimaryButton type="submit" loading={isSubmitting}>
            {t('company.cta.submit')}
          </PrimaryButton>
        </div>
      </form>

      <div className="border-t border-gray-100 my-8" />

      <CompanySectionLayout title={t('company.logo.section_title')}>
        <CompanyLogoSection company={company} />
      </CompanySectionLayout>
    </>
  );
};
